import { lineUnitPrice, weightedLineTotal } from './pricing';

type Channel = 'take_away' | 'delivery' | 'dine_in';

/**
 * Το ποσό που έχει ΗΔΗ η γραμμή (lineOwnAmount) και το μέγεθός της πριν την επεξεργασία. Το δίνει η
 * οθόνη μόνο για γραμμή ανοιχτής τιμής ή ζυγιζόμενου.
 */
export interface EditedLineOwn {
  amount: number | null;
  sizeId: number | string | null | undefined;
}

const validWeight = (weight: number | string | null | undefined): number | null => {
  const w = Number(weight);
  if (weight === null || weight === undefined || (weight as unknown) === '' || !Number.isFinite(w) || w <= 0) return null;
  return w;
};

const sizeKey = (id: number | string | null | undefined): number | null => (id === null || id === undefined ? null : Number(id));

const hasAmount = (own: EditedLineOwn | null | undefined): own is EditedLineOwn & { amount: number } =>
  !!own && own.amount !== null && Number.isFinite(own.amount);

/**
 * Τιμή γραμμής καλαθιού ΠΡΙΝ από έξτρα/επιλογές/έκπτωση, όταν ο ταμίας την ξαναχτίζει
 * (updateBasketItem του TakeAway/DeliveryWithMap, onConfirm της κονσόλας τραπεζιών, υποσύνολο του
 * ProductCustomizeModal).
 *
 * ΓΙΑΤΙ (απόφαση ιδιοκτήτη 2/10/2026 «κιλό × βάρος παντού», P5-E17): η γραμμή ζυγιζόμενου κρατά ως
 * τιμή το ΣΥΝΟΛΟ του βάρους (τιμή κιλού × βάρος, ποσότητα 1). Η επεξεργασία έβαζε στη θέση του τη
 * σκέτη τιμή μονάδας — την τιμή ΕΝΟΣ κιλού. Εδώ:
 *  - ανοιχτή τιμή: το πληκτρολογημένο ποσό (`own.amount`), όχι ο κατάλογος·
 *  - με βάρος, ίδιο μέγεθος: το ποσό της γραμμής όπως μπήκε (`own.amount`). ΟΧΙ ξανά από τον
 *    κατάλογο: η πρώτη είσοδος έχει δική της τιμή κιλού (barcode = του server, κλικ = τιμή του
 *    καναλιού) και δική της στρογγυλοποίηση — αλλαγή μόνο σημείωσης θα άλλαζε ποσό, ως και κάτω
 *    από το κατώτατο (403)·
 *  - με βάρος, άλλο μέγεθος (ή χωρίς `own`): τιμή κιλού × βάρος με τη στρογγυλοποίηση του
 *    κατώτατου (weightedLineTotal)· τιμή κιλού = lineUnitPrice (χωρίς μέγεθος το προεπιλεγμένο)·
 *  - οτιδήποτε άλλο: lineUnitPrice, κανένα ποσό δεν αλλάζει.
 */
export function editedLineBasePrice(
  product: Parameters<typeof lineUnitPrice>[0] & { is_open_price?: boolean | null },
  size: (Parameters<typeof lineUnitPrice>[1] & { id?: number | string }) | null | undefined,
  weight: number | string | null | undefined,
  channel: Channel,
  own?: EditedLineOwn | null
): number {
  if (product.is_open_price && hasAmount(own)) return own.amount;
  const unit = lineUnitPrice(product, size, channel);
  const w = validWeight(weight);
  if (w === null) return unit;
  if (hasAmount(own) && sizeKey(own.sizeId) === sizeKey(size?.id)) return own.amount;
  return weightedLineTotal(unit, w);
}

/**
 * Το ποσό μιας γραμμής ΠΡΙΝ από έξτρα/επιλογές/έκπτωση γραμμής, από τα ποσά της όπως είναι τώρα:
 * τιμή μονάδας χωρίς την έκπτωση γραμμής (`discountPercent`), μείον έξτρα/επιλογές (`addOnsSum`,
 * με τον τύπο της κάθε οθόνης). Για γραμμή με προωθητική ο καλών δίνει την τιμή ΠΡΙΝ την
 * προωθητική (original_unit_price) με έκπτωση 0: το «discount» εκεί είναι το ποσοστό της
 * προωθητικής, όχι έκπτωση πάνω στο original_unit_price.
 *
 * ΓΙΑΤΙ από τα ποσά και όχι από πεδίο: οι γραμμές από πρόχειρα, επανάληψη και συγχώνευση δεν θα
 * είχαν τέτοιο πεδίο, και η πρώτη είσοδος δεν αλλάζει. Στρογγυλοποίηση μόνο του θορύβου της
 * διαίρεσης (6 δεκαδικά): η γραμμή ζυγιζόμενου του delivery από κλικ δεν είναι σε λεπτά, και
 * επεξεργασία χωρίς αλλαγή δεν πρέπει να την αγγίζει. Έκπτωση 100% (ή μη αριθμός) δεν
 * αντιστρέφεται → null, και ο καλών μένει στον τύπο του καταλόγου.
 */
export function lineOwnAmount(unitPrice: number, discountPercent: number | null | undefined, addOnsSum: number): number | null {
  const d = Number(discountPercent) || 0;
  if (!Number.isFinite(unitPrice) || d >= 100 || d < 0) return null;
  const addOns = Number(addOnsSum) || 0;
  if (d === 0 && addOns === 0) return unitPrice >= 0 ? unitPrice : null;
  const beforeDiscount = d > 0 ? unitPrice / (1 - d / 100) : unitPrice;
  const amount = Math.round((beforeDiscount - addOns) * 1e6) / 1e6;
  return Number.isFinite(amount) && amount >= 0 ? amount : null;
}

/** Τα πεδία της γραμμής της κονσόλας τραπεζιών που χρειάζεται η επεξεργασία της. */
export interface DineInEditedLine {
  size_id?: number | null;
  weight?: number | string | null;
  unit_price?: number | null;
  /** Πριν από έκπτωση γραμμής/προωθητική (με έξτρα/επιλογές)· λείπει όταν δεν υπάρχει καμία. */
  original_unit_price?: number | null;
  discount_percent?: number | null;
  notes?: string | null;
  addedExtras?: Array<{ price: number; quantity: number }>;
  selected_options?: Array<{ price_modifier?: number | null }>;
}

/**
 * Κονσόλα τραπεζιών: το ποσό της γραμμής ζυγιζόμενου/ανοιχτής τιμής πριν την επεξεργασία (για το
 * editedLineBasePrice). original_unit_price = πριν την έκπτωση γραμμής/προωθητική, γι' αυτό έκπτωση 0·
 * η έκπτωση γραμμής ξαναμπαίνει από το modal (initialDiscount).
 */
export function dineInLineOwn(line: DineInEditedLine | null | undefined, isOpenPrice: boolean | null | undefined): EditedLineOwn | null {
  if (!line) return null;
  if (validWeight(line.weight) === null && !isOpenPrice) return null;
  return {
    amount: lineOwnAmount(
      Number(line.original_unit_price ?? line.unit_price),
      0,
      (line.addedExtras || []).reduce((s, e) => s + e.price * e.quantity, 0)
        + (line.selected_options || []).reduce((s, o) => s + (o.price_modifier || 0), 0)),
    sizeId: line.size_id ?? null,
  };
}

/**
 * Κονσόλα τραπεζιών, onConfirm του ProductCustomizeModal: τα ποσά της γραμμής (νέας ή υπό επεξεργασία).
 * Ίδιος υπολογισμός με το υποσύνολο του modal, ώστε το test να πιάνει ό,τι αποθηκεύεται στο καλάθι
 * και όχι μόνο ό,τι δείχνει η οθόνη. Νέα γραμμή (null) = σκέτη τιμή dine-in του μεγέθους.
 */
export function dineInCustomizedLinePrices(
  product: Parameters<typeof editedLineBasePrice>[0],
  size: Parameters<typeof editedLineBasePrice>[1],
  editingLine: DineInEditedLine | null | undefined,
  r: {
    selectedOptions: Array<{ price_modifier?: number | null }>;
    addedExtras: Array<{ price: number; quantity: number }>;
    discount: number;
  }
) {
  const sizedUnit = editedLineBasePrice(product, size, editingLine?.weight, 'dine_in', dineInLineOwn(editingLine, product.is_open_price));
  const optionsSum = r.selectedOptions.reduce((s, o) => s + (o.price_modifier || 0), 0);
  const extrasSum = r.addedExtras.reduce((s, e) => s + e.price * e.quantity, 0);
  const originalUnit = sizedUnit + optionsSum + extrasSum;
  const discountedUnit = originalUnit * (1 - r.discount / 100);
  const lineDiscount = originalUnit - discountedUnit; // ανά μονάδα
  return {
    unit_price: parseFloat(discountedUnit.toFixed(2)),
    discount_percent: r.discount > 0 ? r.discount : undefined,
    discount_amount: r.discount > 0 ? parseFloat(lineDiscount.toFixed(2)) : undefined,
    original_unit_price: r.discount > 0 ? parseFloat(originalUnit.toFixed(2)) : undefined,
  };
}

/**
 * Ό,τι περνά η κονσόλα τραπεζιών στο ProductCustomizeModal (null = νέα γραμμή).
 *
 * ΓΙΑΤΙ (απόφαση P5-E18): η έκπτωση γραμμής φαίνεται μόνο σε όποιον έχει dine_in_apply_discount,
 * όπως η έκπτωση παραγγελίας/συνεδρίας. Η υπάρχουσα έκπτωση μιας γραμμής περνά πάντα ως αρχική τιμή:
 * χωρίς δικαίωμα δεν αλλάζει, αλλά ούτε μηδενίζεται σιωπηλά όταν ο υπάλληλος αλλάξει π.χ. μόνο τη σημείωση.
 */
export function dineInLineEditModalProps(line: DineInEditedLine | null, canDiscount: boolean, isOpenPrice?: boolean | null) {
  return {
    showDiscount: canDiscount,
    initialDiscount: Number(line?.discount_percent) > 0 ? Number(line!.discount_percent) : 0,
    initialSpecialInstructions: line?.notes || '',
    weight: validWeight(line?.weight),
    lineOwn: dineInLineOwn(line, isOpenPrice),
  };
}
